import { api } from '../utils/apiClient';
import config from '../config/api.config';
import { 
  ApiResponse, 
  Brand, 
  Branch,
  ShiftSatisfactionData,
  OrderTypeSatisfactionData,
  ShiftSubscriptionData,
  OrderTypeSubscriptionData,
  SubscriptionTrendData,
  DetailedSatisfactionData,
  CategorySatisfaction,
  StaffPerformanceData,
  KpiData,
  ExecutiveDashboardData,
  TimePeriod
} from '../types/api';
import { ClubConditionsResponse } from '../types/clubConditions';

const API_BASE_URL = `${config.API_URL}`; // Change to your actual API URL

// Base URL paths
const API_PATHS = {
  // Core entities
  BRANDS: `/brandsee`,
  BRANCHES: `/branches`,
  
  // Dashboard data
  DASHBOARD: `/dashboard`,
  SATISFACTION_BY_SHIFT: `/satisfaction-by-shift`,
  SATISFACTION_BY_ORDER_TYPE: `/satisfaction-by-order-type`,
  DETAILED_SATISFACTION: `/detailed-satisfaction`,
  SATISFACTION_METRICS_BY_CATEGORY: `/satisfaction-metrics-by-category`,
  SATISFACTION_CATEGORIES: `/satisfaction-categories`,
  SUBSCRIPTION_BY_SHIFT: `/subscription-by-shift`,
  SUBSCRIPTION_BY_ORDER_TYPE: `/subscription-by-order-type`,
  KPI: `/dashboard/:branchId/kpi`,
  
  // Staff performance
  STAFF_PERFORMANCE: `/staff-performance`,
  
  // Club dashboard
  CLUB_DASHBOARD: `/club-dashboard`,
  CLUB_BRAND: `/club-dashboard/brand`,
  CLUB_BRANCH: `/club-dashboard/branch`,
  CLUB_BRANCH_AVERAGE: `/club-dashboard/branch/average`,
  CLUB_ALL_RESTAURANTS: `/club-dashboard/all-restaurants`,
  CLUB_ALL_RESTAURANTS_PARTIAL_LATEST: `/club-dashboard/all-restaurants-partial-latest`,
  CLUB_ALL_RESTAURANTS_YOY: `/club-dashboard/all-restaurants-yoy`,
  CLUB_KPI_THRESHOLDS: `/club-dashboard/kpi-thresholds`,
  CLUB_MARKET_TOTAL_INVOICES: `/club-dashboard/market-total-invoices`,

  MARKET_PULSE_DATA: `/market-pulse/data`,
  MARKET_PULSE_WAITLIST: `/market-pulse/waitlist`,

  // Club conditions dashboard
  CLUB_CONDITIONS: `/club-conditions`,

  // Customer grouping dashboard
  CUSTOMER_GROUPING: `/customer-grouping`,
  
  // Executive dashboard
  EXECUTIVE_DASHBOARD: `/executive-dashboard`,
  EXECUTIVE_KPIS: `/executive-dashboard/:branchId/kpis`,
  EXECUTIVE_FUNNEL: `/executive-dashboard/:branchId/funnel`,
  
  // Evaluation report
  EVALUATION_REPORT: `/evaluation-report`,
  ALL_RESTAURANTS_AVERAGE: `/evaluation-report/all-restaurants-average`,
  BRAND_AVERAGE: `/evaluation-report/brand-average`,
};

// Utility function to replace URL parameters
const replaceUrlParams = (url: string, params: Record<string, string | number>): string => {
  let result = url;
  Object.entries(params).forEach(([key, value]) => {
    result = result.replace(`:${key}`, String(value));
  });
  return result;
};

// API services organized by domain
export const refineCustomerApi = {
  getDashboardData: (branchId: number) => 
    api.get<ApiResponse<any>>(`/refine-customer-dashboard/${branchId}`),
  
  // Granular endpoints
  getSurveyCount: (branchId: number) =>
    api.get<ApiResponse<any>>(`/refine-customer-dashboard/${branchId}/survey-count`),
  
  getResponsesByType: (branchId: number) =>
    api.get<ApiResponse<any>>(`/refine-customer-dashboard/${branchId}/responses-by-type`),
  
  getRegisteredReasons: (branchId: number) =>
    api.get<ApiResponse<any>>(`/refine-customer-dashboard/${branchId}/registered-reasons`),
  
  getDissatisfactionReasons: (branchId: number, limit: number = 10) =>
    api.get<ApiResponse<any>>(`/refine-customer-dashboard/${branchId}/dissatisfaction-reasons?limit=${limit}`),
  
  getDissatisfiedProducts: (branchId: number) =>
    api.get<ApiResponse<any>>(`/refine-customer-dashboard/${branchId}/dissatisfied-products`),
  
  getComplaintStatus: (branchId: number) =>
    api.get<ApiResponse<any>>(`/refine-customer-dashboard/${branchId}/complaint-status`),
  
  getSurveyResponsesByAnswer: (branchId: number) =>
    api.get<ApiResponse<any>>(`/refine-customer/survey-responses-by-answer/${branchId}`),
  
  getDissatisfactionDetails: (branchId: number, categoryName: string) =>
    api.get<ApiResponse<any>>(`/refine-customer-dashboard/${branchId}/dissatisfaction-details?category=${encodeURIComponent(categoryName)}`),
  
  getTotalResponses: (branchId: number) =>
    api.get<ApiResponse<any>>(`/refine-customer-dashboard/${branchId}/total-responses`)
};

export const brandsApi = {
  getAll: () => api.get<ApiResponse<Brand[]>>(API_PATHS.BRANDS),
  getById: (brandId: number) => api.get<ApiResponse<Brand>>(`${API_PATHS.BRANDS}/${brandId}`)
};

export const branchesApi = {
  getByBrand: (brandId: number) => 
    api.get<ApiResponse<Branch[]>>(`${API_PATHS.BRANCHES}/brand/${brandId}`),
  getById: (branchId: number) => 
    api.get<ApiResponse<Branch>>(`${API_PATHS.BRANCHES}/${branchId}`)
};

// Helper function to convert TimePeriod to months
const timeRangeToMonths = (timeRange: TimePeriod): number => {
  switch (timeRange) {
    case '1month': return 1;
    case '3months': return 3;
    case '6months': return 6;
    case '12months': return 12;
    default: return 6;
  }
};

export const satisfactionApi = {
  getKpis: (branchId: number, timeRange: TimePeriod = '12months') => 
    api.get<ApiResponse<any>>(
      `/satisfaction/kpis/${branchId}?months=${timeRangeToMonths(timeRange)}`
    ),
  
  getOverallTrend: (branchId: number, timeRange: TimePeriod = '12months') => 
    api.get<ApiResponse<any>>(
      `/satisfaction/overall-trend/${branchId}?months=${timeRangeToMonths(timeRange)}`
    ),
  
  getByShift: (branchId: number, timeRange: TimePeriod = '12months') => 
    api.get<ApiResponse<ShiftSatisfactionData[]>>(
      `${API_PATHS.SATISFACTION_BY_SHIFT}/${branchId}?months=${timeRangeToMonths(timeRange)}`
    ),
  
  getByOrderType: (branchId: number, timeRange: TimePeriod = '12months') => 
    api.get<ApiResponse<OrderTypeSatisfactionData[]>>(
      `${API_PATHS.SATISFACTION_BY_ORDER_TYPE}/${branchId}?months=${timeRangeToMonths(timeRange)}`
    ),
  
  getDetailed: (branchId: number, timeRange: TimePeriod = '12months') => 
    api.get<ApiResponse<DetailedSatisfactionData[]>>(
      `${API_PATHS.DETAILED_SATISFACTION}/${branchId}?months=${timeRangeToMonths(timeRange)}`
    ),
  
  getByCategory: (branchId: number, timeRange: TimePeriod = '12months') => 
    api.get<ApiResponse<CategorySatisfaction[]>>(
      `${API_PATHS.SATISFACTION_METRICS_BY_CATEGORY}/${branchId}?months=${timeRangeToMonths(timeRange)}`
    ),
  
  getCategories: (branchId?: number) => 
    api.get<ApiResponse<any>>(
      `${API_PATHS.SATISFACTION_CATEGORIES}${branchId ? `?branch_id=${branchId}` : ''}`
    )
};

export const subscriptionApi = {
  getOverallTrend: (branchId: number, timeRange: TimePeriod = '12months') =>
    api.get<ApiResponse<SubscriptionTrendData[]>>(
      `/subscription/overall-trend/${branchId}?months=${timeRangeToMonths(timeRange)}`
    ),

  getByShift: (branchId: number, timeRange: TimePeriod = '12months') =>
    api.get<ApiResponse<ShiftSubscriptionData[]>>(
      `${API_PATHS.SUBSCRIPTION_BY_SHIFT}/${branchId}?months=${timeRangeToMonths(timeRange)}`
    ),

  getByOrderType: (branchId: number, timeRange: TimePeriod = '12months') =>
    api.get<ApiResponse<OrderTypeSubscriptionData[]>>(
      `${API_PATHS.SUBSCRIPTION_BY_ORDER_TYPE}/${branchId}?months=${timeRangeToMonths(timeRange)}`
    )
};

export const staffApi = {
  getPerformance: (brandId: number, branchId: number, timeRange: TimePeriod = '6months') => 
    api.get<ApiResponse<StaffPerformanceData[]>>(
      `${API_PATHS.STAFF_PERFORMANCE}?brandId=${brandId}&branchId=${branchId}&months=${timeRangeToMonths(timeRange)}`
    ),
  
  getPerformanceLegacy: (branchId: number, timeRange: TimePeriod = '6months') => 
    api.get<ApiResponse<StaffPerformanceData[]>>(`${API_PATHS.STAFF_PERFORMANCE}/${branchId}?months=${timeRangeToMonths(timeRange)}`)
};

export const dashboardApi = {
  getKpi: (branchId: number, timeRange: TimePeriod = '6months') => 
    api.get<ApiResponse<KpiData[]>>(
      replaceUrlParams(API_PATHS.KPI, { branchId }) + `?months=${timeRangeToMonths(timeRange)}`
    ),
  
  getDashboardData: (branchId: number, timeRange: TimePeriod = '6months') => 
    api.get<ApiResponse<any>>(`${API_PATHS.DASHBOARD}/${branchId}?months=${timeRangeToMonths(timeRange)}`)
};

export const clubApi = {
  getDashboardData: (branchId: number, timeRange: TimePeriod = '12months') => 
    api.get<ApiResponse<any>>(`${API_PATHS.CLUB_DASHBOARD}/${branchId}?months=${timeRangeToMonths(timeRange)}`),
  
  getBranchData: (branchId: number, timeRange: TimePeriod = '12months') => 
    api.get<ApiResponse<any>>(`${API_PATHS.CLUB_BRANCH}/${branchId}?months=${timeRangeToMonths(timeRange)}`),

  getBranchAverageData: (branchId: number, timeRange: TimePeriod = '12months') =>
    api.get<ApiResponse<any>>(`${API_PATHS.CLUB_BRANCH_AVERAGE}/${branchId}?months=${timeRangeToMonths(timeRange)}`),
  
  getBrandData: (brandId: number, timeRange: TimePeriod = '12months') => 
    api.get<ApiResponse<any>>(`${API_PATHS.CLUB_BRAND}/${brandId}?months=${timeRangeToMonths(timeRange)}`),
  
  getAllRestaurantsData: (timeRange: TimePeriod = '12months') =>
    api.get<ApiResponse<any>>(`${API_PATHS.CLUB_ALL_RESTAURANTS}?months=${timeRangeToMonths(timeRange)}`),

  getKpiThresholds: (timeRange: TimePeriod = '12months') =>
    api.get<ApiResponse<any>>(`${API_PATHS.CLUB_KPI_THRESHOLDS}?months=${timeRangeToMonths(timeRange)}`),

  // Raw-months variants (used by Market Pulse, which fetches one extra month
  // so the first displayed month's month-over-month growth can be computed)
  getDashboardDataByMonths: (branchId: number, months: number) =>
    api.get<ApiResponse<any>>(`${API_PATHS.CLUB_DASHBOARD}/${branchId}?months=${months}`),

  getAllRestaurantsDataByMonths: (months: number) =>
    api.get<ApiResponse<any>>(`${API_PATHS.CLUB_ALL_RESTAURANTS}?months=${months}`),

  getAllRestaurantsPartialLatestByMonths: (months: number) =>
    api.get<ApiResponse<any>>(`${API_PATHS.CLUB_ALL_RESTAURANTS_PARTIAL_LATEST}?months=${months}`),

  getAllRestaurantsYoYDataByMonths: (months: number) =>
    api.get<ApiResponse<any>>(`${API_PATHS.CLUB_ALL_RESTAURANTS_YOY}?months=${months}`),

  /** مجموع کل فاکتورهای ثبت‌شده در BI_BranchDetails — شاخص اعتماد نبض بازار */
  getMarketTotalInvoiceCount: () =>
    api.get<ApiResponse<{ totalInvoiceCount: number }>>(API_PATHS.CLUB_MARKET_TOTAL_INVOICES),
};

export const marketPulseApi = {
  getDashboardData: (branchId: number, brandId?: number) => {
    const params = new URLSearchParams({ branch_id: String(branchId) });
    if (brandId) params.append('brand_id', String(brandId));
    return api.get<ApiResponse<any>>(`${API_PATHS.MARKET_PULSE_DATA}?${params.toString()}`);
  },

  joinWaitlist: (payload: {
    email?: string;
    mobile?: string;
    brandId?: number;
    branchId?: number;
    brandName?: string;
    branchName?: string;
    userName?: string;
  }) =>
    api.post<ApiResponse<{ message: string }>>(API_PATHS.MARKET_PULSE_WAITLIST, {
      email: payload.email,
      mobile: payload.mobile,
      brand_id: payload.brandId,
      branch_id: payload.branchId,
      brand_name: payload.brandName,
      branch_name: payload.branchName,
      user_name: payload.userName,
    }),
};

export const clubConditionsApi = {
  getClubConditionsData: (branchId: number, timeRange: TimePeriod = '12months') => 
    api.get<ClubConditionsResponse>(`${API_PATHS.CLUB_CONDITIONS}/${branchId}?months=${timeRangeToMonths(timeRange)}`)
};

export const customerGroupingApi = {
  getDashboardData: (brandId: number, branchId?: number) => {
    const queryParams = new URLSearchParams();
    // if (branchId) queryParams.append('branchId', String(branchId));
    const queryString = queryParams.toString() ? `?${queryParams.toString()}` : '';
    return api.get<ApiResponse<any>>(`${API_PATHS.CUSTOMER_GROUPING}/${brandId}`);
  }
};

export const executiveApi = {
  getDashboardData: (branchId: number, params: { 
    timeRange?: TimePeriod;
    start_month?: string; 
    end_month?: string;
    year_month?: string;
  } = {}) => {
    const queryParams = new URLSearchParams();
    
    if (params.timeRange){
      let months = 12;
      if (params.timeRange === "12months")
        months = 12
      else if (params.timeRange === "1month")
        months = 1
      else if(params.timeRange === "3months")
        months = 3
      else if (params.timeRange === "6months")
        months = 6

      queryParams.append('months', `${months}`);
    }

    
    // if (params.start_month) queryParams.append('start_month', params.start_month);
    // if (params.end_month) queryParams.append('end_month', params.end_month);
    // if (params.year_month) queryParams.append('year_month', params.year_month);
    
    const queryString = queryParams.toString() ? `?${queryParams.toString()}` : '';
    return api.get<ApiResponse<ExecutiveDashboardData>>(`${API_PATHS.EXECUTIVE_DASHBOARD}/${branchId}${queryString}`);
  },
  
  getKpis: (branchId: number, params: { timeRange?: TimePeriod; yearMonth?: string } = {}) => {
    const queryParams = new URLSearchParams();
    
    if (params.timeRange) queryParams.append('months', String(timeRangeToMonths(params.timeRange)));
    if (params.yearMonth) queryParams.append('year_month', params.yearMonth);
    
    const queryString = queryParams.toString() ? `?${queryParams.toString()}` : '';
    return api.get<ApiResponse<any>>(
      replaceUrlParams(API_PATHS.EXECUTIVE_KPIS, { branchId }) + queryString
    );
  },
  
  getFunnelData: (branchId: number, params: { timeRange?: TimePeriod; yearMonth?: string } = {}) => {
    const queryParams = new URLSearchParams();
    
    if (params.timeRange) queryParams.append('months', String(timeRangeToMonths(params.timeRange)));
    if (params.yearMonth) queryParams.append('year_month', params.yearMonth);
    
    const queryString = queryParams.toString() ? `?${queryParams.toString()}` : '';
    return api.get<ApiResponse<any>>(
      replaceUrlParams(API_PATHS.EXECUTIVE_FUNNEL, { branchId }) + queryString
    );
  },
  
  // New granular endpoints
  getCreditUsageRate: (branchId: number, params: { timeRange?: TimePeriod } = {}) => {
    const queryParams = new URLSearchParams();
    if (params.timeRange) queryParams.append('months', String(timeRangeToMonths(params.timeRange)));
    const queryString = queryParams.toString() ? `?${queryParams.toString()}` : '';
    return api.get<ApiResponse<any>>(`${API_PATHS.EXECUTIVE_DASHBOARD}/${branchId}/credit-usage-rate${queryString}`);
  },
  
  getKeyInsights: (branchId: number, params: { timeRange?: TimePeriod } = {}) => {
    const queryParams = new URLSearchParams();
    if (params.timeRange) queryParams.append('months', String(timeRangeToMonths(params.timeRange)));
    const queryString = queryParams.toString() ? `?${queryParams.toString()}` : '';
    return api.get<ApiResponse<any>>(`${API_PATHS.EXECUTIVE_DASHBOARD}/${branchId}/key-insights${queryString}`);
  },
  
  getCreditComparison: (branchId: number, params: { timeRange?: TimePeriod } = {}) => {
    const queryParams = new URLSearchParams();
    if (params.timeRange) queryParams.append('months', String(timeRangeToMonths(params.timeRange)));
    const queryString = queryParams.toString() ? `?${queryParams.toString()}` : '';
    return api.get<ApiResponse<any>>(`${API_PATHS.EXECUTIVE_DASHBOARD}/${branchId}/credit-comparison${queryString}`);
  },
  
  getTrendAnalysis: (branchId: number, params: { timeRange?: TimePeriod } = {}) => {
    const queryParams = new URLSearchParams();
    if (params.timeRange) queryParams.append('months', String(timeRangeToMonths(params.timeRange)));
    const queryString = queryParams.toString() ? `?${queryParams.toString()}` : '';
    return api.get<ApiResponse<any>>(`${API_PATHS.EXECUTIVE_DASHBOARD}/${branchId}/trend-analysis${queryString}`);
  },
  
  getPerformanceTable: (branchId: number, params: { timeRange?: TimePeriod } = {}) => {
    const queryParams = new URLSearchParams();
    if (params.timeRange) queryParams.append('months', String(timeRangeToMonths(params.timeRange)));
    const queryString = queryParams.toString() ? `?${queryParams.toString()}` : '';
    return api.get<ApiResponse<any>>(`${API_PATHS.EXECUTIVE_DASHBOARD}/${branchId}/performance-table${queryString}`);
  }
};

export const evaluationApi = {
  getReportData: (branchId: number, params: { timeRange?: TimePeriod; startDate?: string; endDate?: string; } = {}) => {
    const queryParams = new URLSearchParams();
    if (params.timeRange) queryParams.append('months', String(timeRangeToMonths(params.timeRange)));
    if (params.startDate) queryParams.append('start_date', params.startDate);
    if (params.endDate) queryParams.append('end_date', params.endDate);
    
    const queryString = queryParams.toString() ? `?${queryParams.toString()}` : '';
    return api.get<ApiResponse<any>>(`${API_PATHS.EVALUATION_REPORT}/${branchId}${queryString}`);
  },
  
  getAllRestaurantsAverage: (params: { timeRange?: TimePeriod; startDate?: string; endDate?: string; } = {}) => {
    const queryParams = new URLSearchParams();
    if (params.timeRange) queryParams.append('months', String(timeRangeToMonths(params.timeRange)));
    if (params.startDate) queryParams.append('start_date', params.startDate);
    if (params.endDate) queryParams.append('end_date', params.endDate);
    
    const queryString = queryParams.toString() ? `?${queryParams.toString()}` : '';
    return api.get<ApiResponse<any>>(`${API_PATHS.ALL_RESTAURANTS_AVERAGE}${queryString}`);
  },
  
  getBrandAverage: (brandId: number, params: { timeRange?: TimePeriod; startDate?: string; endDate?: string; } = {}) => {
    const queryParams = new URLSearchParams();
    if (params.timeRange) queryParams.append('months', String(timeRangeToMonths(params.timeRange)));
    if (params.startDate) queryParams.append('start_date', params.startDate);
    if (params.endDate) queryParams.append('end_date', params.endDate);
    
    const queryString = queryParams.toString() ? `?${queryParams.toString()}` : '';
    return api.get<ApiResponse<any>>(`${API_PATHS.BRAND_AVERAGE}/${brandId}${queryString}`);
  }
};
